import { asText, Content, isFilled } from '@prismicio/client';
import { PrismicNextImage, PrismicNextLink } from '@prismicio/next';
import { PrismicRichText, SliceComponentProps } from '@prismicio/react';
import { Url } from 'url';
import { Carousel } from '@/components';
import { createClient } from '@/prismicio';

/**
 * Props for `CaseStudies`.
 */
export type CaseStudiesProps = SliceComponentProps<Content.CaseStudiesSlice>;

/**
 * Component for "CaseStudies" Slices.
 */
const CaseStudies = async ({
  slice,
}: CaseStudiesProps): Promise<JSX.Element> => {
  const client = createClient();
  const caseStudyIDs = slice.primary.cards
    .map((card) => {
      if (isFilled.contentRelationship(card.case_study)) {
        return card.case_study.id;
      }
      return null;
    })
    .filter((id): id is string => id !== null);

  let caseStudies: Content.CaseStudyDocument[] = [];
  if (caseStudyIDs.length > 0) {
    caseStudies = await client.getAllByIDs<Content.CaseStudyDocument>(
      caseStudyIDs,
      {
        fetch: [
          'case_study.name',
          'case_study.description',
          'case_study.image',
        ],
      },
    );
  }

  const caseStudiesById = new Map(caseStudies.map((cs) => [cs.id, cs.data]));

  const renderColumns = () => {
    const cards = slice.primary.cards;
    return cards.map((card, card_index) => {
      const isFirstCard = card_index === 0;

      if (!isFilled.contentRelationship(card.case_study)) {
        return null;
      }

      const typedCaseStudyData = caseStudiesById.get(card.case_study.id);

      if (!typedCaseStudyData) {
        return null;
      }

      return (
        <div
          key={`card-${card_index}`}
          className={`card ${
            isFirstCard
              ? 'lg:w-1/2 lg:flex-row md:flex-col sm:flex-col'
              : 'lg:w-1/4 flex-col'
          }`}
        >
          <div
            className={`h-48 pl-6 pr-6 flex flex-col ${
              isFirstCard
                ? 'lg:h-full w-full lg:w-1/2 lg:justify-center lg:items-center pt-6 md:pt-6 lg:pt-0'
                : 'lg:w-full justify-left pt-6 md:pt-6 lg:pt-12'
            }`}
          >
            <div className="justify-left">
              <div
                className={`font-bold ${
                  isFirstCard ? 'text-3xl' : 'text-2xl'
                } pb-4`}
              >
                {asText(typedCaseStudyData?.name)}
              </div>
              <div className="text-sm pb-4">
                {asText(typedCaseStudyData?.description)}
              </div>
              <PrismicNextLink
                href={card.case_study.url as unknown as Url}
                className="text-white button-outline-primary font-normal"
              >
                <span>{card.button_label}</span>
              </PrismicNextLink>
            </div>
          </div>
          <div
            className={`${' '}
              isFirstCard ? 'h-48 lg:w-1/2 lg:h-full' : 'lg:w-full h-48'
            } overflow-hidden`}
          >
            <PrismicNextImage
              field={typedCaseStudyData?.image}
              className={`${' '}
                isFirstCard
                  ? 'rounded-b-[20px] lg:rounded-r-[20px] lg:rounded-l-[0px] w-full h-full'
                  : 'rounded-b-[20px] w-full h-full'
              } object-cover object-center`}
            />
          </div>
        </div>
      );
    });
  };

  return (
    <section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      className="text-white px-5 py-16 md:px-16 md:py-24 flex flex-col max-w-screen-2xl mx-auto"
    >
      <div className="text-center flex flex-col md:gap-6 gap-3 mb-12">
        <PrismicRichText field={slice.primary.title} />
        <PrismicRichText field={slice.primary.description} />
      </div>
      <div className="lg:hidden flex flex-col">
        <Carousel
          arrows={false}
          renderDotsOutside
          dotType="compact-dark"
          dotExtraClass="static mt-4 mb-[53px]"
        >
          {renderColumns()}
        </Carousel>
      </div>
      <div className="hidden md:hidden lg:flex gap-8 flex-row mb-12">
        {renderColumns()}
      </div>
      <div className="flex justify-center">
        <PrismicNextLink
          field={slice.primary.button_link}
          className="button-outline-primary"
        >
          <span>{slice.primary.button_label}</span>
        </PrismicNextLink>
      </div>
    </section>
  );
};

export default CaseStudies;
